import React, { useState, useEffect, useCallback } from 'react';
import ProductAutocomplete from './ProductAutocomplete';
import OfficialBadge from './OfficialBadge';
import ScoreSlider from './ScoreSlider';
import AttachmentPicker from './AttachmentPicker';
import { useI18n } from './i18n';
import { GBT_CATEGORIES, gbtFactors, gbtWeightedScore } from './gbtTea';

const DRAFT_KEY = 'tnh2_draft_tea';

export const TEA_TYPES = ['green', 'white', 'yellow', 'oolong', 'black', 'dark', 'herbal', 'other'];
// The five factors that make up the total (out of 50); `overall` is a separate impression
export const TEA_FACTORS = ['dry_leaf', 'liquor_colour', 'aroma', 'taste', 'infused_leaf'];
const RATINGS = [...TEA_FACTORS, 'overall'];

const INITIAL = {
  product_name: '', producer: '', origin: '', variety: '', harvest_year: '',
  sample_code: '', tea_type: '', gbt_category: '',
  leaf_grams: '2', water_ml: '100', water_temperature: '', steep_seconds: '360',
  dry_leaf: null, liquor_colour: null, aroma: null, taste: null, infused_leaf: null, overall: null,
  tasting_date: new Date().toISOString().slice(0, 10), venue: '', taster: '',
  notes: '', visibility: 'private',
};

function loadDraft() {
  try {
    const saved = localStorage.getItem(DRAFT_KEY);
    if (saved) return { ...INITIAL, ...JSON.parse(saved) };
  } catch { /* ignore */ }
  return INITIAL;
}

// Form state from an API sheet (null values keep the empty default; numbers become strings for the inputs)
const NUMBER_INPUTS = ['harvest_year', 'leaf_grams', 'water_ml', 'water_temperature', 'steep_seconds'];
function fromSheet(sheet) {
  const out = { ...INITIAL, leaf_grams: '', water_ml: '' };
  Object.keys(INITIAL).forEach((k) => {
    if (sheet[k] === undefined || sheet[k] === null) return;
    out[k] = NUMBER_INPUTS.includes(k) ? String(sheet[k]) : sheet[k];
  });
  return out;
}

const emptyToNull = (value) => (value === '' || value === undefined ? null : value);
const toNumber = (value, parse) => (value === '' || value === null || value === undefined ? null : parse(value));

// Total out of 50, only once the five factors are all scored
export function teaTotal(f) {
  if (TEA_FACTORS.some((k) => f[k] === null || f[k] === undefined || f[k] === '')) return null;
  return TEA_FACTORS.reduce((sum, k) => sum + Number(f[k]), 0);
}

function TeaForm({ onSubmit, initialData, onCancel }) {
  const { t, tc, formatNumber } = useI18n();
  const editing = !!initialData;
  const [form, setForm] = useState(() => (initialData ? fromSheet(initialData) : loadDraft()));
  const [attachmentItems, setAttachmentItems] = useState([]);

  useEffect(() => { if (!editing) localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); }, [form, editing]);

  const handleChange = useCallback((name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);
  const handleInput = (e) => handleChange(e.target.name, e.target.value);

  const handleReset = () => {
    if (!window.confirm(t('tea.confirm_reset'))) return;
    setForm(INITIAL);
    setAttachmentItems([]);
    localStorage.removeItem(DRAFT_KEY);
  };

  const total = teaTotal(form);
  const factors = form.gbt_category ? gbtFactors(form.gbt_category) : [];
  const weighted = form.gbt_category ? gbtWeightedScore(form.gbt_category, form) : null;
  const bag = form.gbt_category === 'tea_bag';
  const powder = form.gbt_category === 'powder';        // the standard has no infused-leaf factor for powder
  const ratingKey = (kind, key) => (bag && (key === 'dry_leaf' || key === 'infused_leaf') ? `tea.${kind}_bag.${key}` : `tea.${kind}.${key}`);
  const visibleRatings = RATINGS.filter((key) => !(powder && key === 'infused_leaf'));

  // Sheet data in the API format (computed fields such as the total are never sent)
  const buildData = () => {
    if (!form.product_name.trim()) { alert(t('tea.alert_product_name')); return null; }
    const data = {
      ...form,
      harvest_year: toNumber(form.harvest_year, (v) => parseInt(v, 10)),
      leaf_grams: toNumber(form.leaf_grams, parseFloat),
      water_ml: toNumber(form.water_ml, parseFloat),
      water_temperature: toNumber(form.water_temperature, parseFloat),
      steep_seconds: toNumber(form.steep_seconds, (v) => parseInt(v, 10)),
    };
    if (powder) data.infused_leaf = null;
    Object.keys(data).forEach((k) => { if (typeof data[k] === 'string') data[k] = emptyToNull(data[k]); });
    return data;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = buildData();
    if (!data) return;
    onSubmit(data, attachmentItems);
    if (editing) return;
    setForm(INITIAL);
    setAttachmentItems([]);
    localStorage.removeItem(DRAFT_KEY);
  };

  return (
    <form className="sheet-form" onSubmit={handleSubmit}>
      <OfficialBadge status="unofficial" source={t('tea.badge_source')} note={t('tea.badge_note')} sourceNote={t('products.source_note.tea')} />

      {/* ── Tea ── */}
      <fieldset>
        <legend>{t('tea.sec.info')}</legend>
        <div className="form-grid">
          <div className="form-field">
            <label>{t('tea.f.product_name')}</label>
            <ProductAutocomplete
              category="tea"
              value={form.product_name}
              required
              onChange={(fields) => setForm((prev) => ({ ...prev, ...fields }))}
            />
          </div>
          <div className="form-field">
            <label>{t('tea.f.producer')}</label>
            <input name="producer" value={form.producer} onChange={handleInput} maxLength={200} />
          </div>
          <div className="form-field">
            <label>{t('tea.f.origin')}</label>
            <input name="origin" value={form.origin} onChange={handleInput} maxLength={200} />
          </div>
          <div className="form-field">
            <label>{t('tea.f.variety')}</label>
            <input name="variety" value={form.variety} onChange={handleInput} maxLength={200} />
          </div>
          <div className="form-field">
            <label>{t('tea.f.harvest_year')}</label>
            <input name="harvest_year" type="number" min={1900} max={2200} value={form.harvest_year} onChange={handleInput} />
          </div>
          <div className="form-field">
            <label>{t('tea.f.sample_code')}</label>
            <input name="sample_code" value={form.sample_code} onChange={handleInput} maxLength={100} />
          </div>
          <div className="form-field">
            <label>{t('tea.f.tea_type')}</label>
            <div className="chip-group">
              {TEA_TYPES.map((type) => (
                <button type="button" key={type}
                  className={`chip ${form.tea_type === type ? 'selected' : ''}`}
                  onClick={() => handleChange('tea_type', form.tea_type === type ? '' : type)}>{tc('tea.type', type)}</button>
              ))}
            </div>
          </div>
          <div className="form-field">
            <label>{t('tea.f.gbt_category')}</label>
            <select name="gbt_category" value={form.gbt_category} onChange={handleInput}>
              <option value="">{t('tea.gbt.none')}</option>
              {GBT_CATEGORIES.map((c) => <option key={c} value={c}>{tc('tea.gbt', c)}</option>)}
            </select>
            <small className="import-hint">{t('tea.gbt.hint')}</small>
          </div>
          <div className="form-field">
            <label>{t('tea.f.tasting_date')}</label>
            <input name="tasting_date" type="date" value={form.tasting_date} onChange={handleInput} />
          </div>
          <div className="form-field">
            <label>{t('tea.f.venue')}</label>
            <input name="venue" value={form.venue} onChange={handleInput} maxLength={200} />
          </div>
          <div className="form-field">
            <label>{t('tea.f.taster')}</label>
            <input name="taster" value={form.taster} onChange={handleInput} maxLength={200} />
          </div>
        </div>
      </fieldset>

      {/* ── Brewing (free parameters) ── */}
      <fieldset>
        <legend>{t('tea.sec.brewing')}</legend>
        <small className="import-hint">{t('tea.brewing_hint')}</small>
        <div className="form-grid">
          <div className="form-field">
            <label>{t('tea.f.leaf_grams')}</label>
            <input name="leaf_grams" type="number" step={0.1} min={0} value={form.leaf_grams} onChange={handleInput} />
          </div>
          <div className="form-field">
            <label>{t('tea.f.water_ml')}</label>
            <input name="water_ml" type="number" step={1} min={0} value={form.water_ml} onChange={handleInput} />
          </div>
          <div className="form-field">
            <label>{t('tea.f.water_temperature')}</label>
            <input name="water_temperature" type="number" step={1} min={0} max={100} value={form.water_temperature} onChange={handleInput} />
          </div>
          <div className="form-field">
            <label>{t('tea.f.steep_seconds')}</label>
            <input name="steep_seconds" type="number" step={1} min={0} value={form.steep_seconds} onChange={handleInput} />
          </div>
        </div>
      </fieldset>

      {/* ── Evaluation ── */}
      <fieldset>
        <legend>{t('tea.sec.evaluation')}</legend>
        {factors.length > 0 && (
          <table className="catalog-table gbt-weights">
            <caption>{t('tea.gbt.weights_title', { category: tc('tea.gbt', form.gbt_category) })}</caption>
            <tbody>
              {factors.map(({ factor, weight }) => (
                <tr key={factor}><td>{t(ratingKey('r', factor))}</td><td>{weight}%</td></tr>
              ))}
            </tbody>
          </table>
        )}
        {visibleRatings.map((key) => (
          <ScoreSlider key={key} label={t(ratingKey('r', key))} hint={t(ratingKey('hint', key))}
            value={form[key]} onChange={(v) => handleChange(key, v)} min={0} max={10} step={0.5} unit="/10" />
        ))}
        <div className="onav-final-total">
          {form.gbt_category ? (
            <>
              <span className="onav-score">
                {weighted === null ? t('tea.weighted_pending') : `${formatNumber(weighted, 1)}/100`}
              </span>
              <small className="import-hint">{t('tea.weighted_hint')}</small>
            </>
          ) : (
            <>
              <span className="onav-score">
                {total === null ? t('tea.total_pending') : `${formatNumber(total, 1)}/50`}
              </span>
              <small className="import-hint">{t('tea.total_hint')}</small>
            </>
          )}
        </div>
        <div className="form-field">
          <label>{t('tea.f.notes')}</label>
          <textarea name="notes" value={form.notes} onChange={handleInput} rows={3} placeholder={t('tea.ph.notes')} />
        </div>
      </fieldset>

      {/* ── Attachments (only when creating: an existing sheet manages them in its detail view) ── */}
      {!editing && (
        <fieldset>
          <legend>{t('attachments.title')}</legend>
          <AttachmentPicker items={attachmentItems} onChange={setAttachmentItems} />
        </fieldset>
      )}

      <div className="form-field publish-opt">
        <label className="publish-label">
          <input type="checkbox" checked={form.visibility === 'public'}
            onChange={(e) => handleChange('visibility', e.target.checked ? 'public' : 'private')} />
          {' '}{t('ui.publish_label')}
        </label>
        <small className="import-hint">{t('ui.publish_hint')}</small>
      </div>

      <div className="form-buttons">
        <button type="submit" className="submit-btn">{editing ? t('tea.save_edit') : t('tea.save_new')}</button>
        {editing
          ? <button type="button" className="reset-btn" onClick={onCancel}>{t('ui.cancel')}</button>
          : <button type="button" className="reset-btn" onClick={handleReset}>{t('ui.reset')}</button>}
      </div>
    </form>
  );
}

export default TeaForm;
